import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseClient } from '@/lib/supabase-client';

export interface AppNotification {
  id: string;
  user_id: string;
  organization_id: string | null;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  read: boolean;
  created_at: string;
}

const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

async function fetchNotifications(): Promise<AppNotification[]> {
  const { data, error } = await supabaseClient
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []) as AppNotification[];
}

async function markAsRead(id: string): Promise<void> {
  const { error } = await supabaseClient
    .from('notifications')
    .update({ read: true })
    .eq('id', id);

  if (error) throw error;
}

async function markAllAsRead(): Promise<void> {
  const { error } = await supabaseClient
    .from('notifications')
    .update({ read: true })
    .eq('read', false);

  if (error) throw error;
}

/**
 * Fetches notifications for the current user (RLS filters by auth.uid()).
 * Only run when user has Supabase session (e.g. authUserId present).
 */
export function useNotificationsList(enabled: boolean) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: fetchNotifications,
    enabled,
    staleTime: 60 * 1000,
  });

  const markAsReadMutation = useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });

  const notifications = query.data ?? [];
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    isLoading: query.isLoading,
    error: query.error,
    unreadCount,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    refetch: query.refetch,
  };
}
